import { forwardRef, ReactNode } from 'react';

import { Breakpoint, styled } from '@mui/material/styles';

import MuiTableContainer, { TableContainerProps as MuiTableContainerProps } from '@mui/material/TableContainer';

import { BoxProps } from '../Box';
import { defaultPaperPaddings } from '../Paper';
import { tableCellClasses } from '../TableCell';
import { typographyClasses } from '../Typography';

export * from '@mui/material/TableContainer';

export interface TableContainerProps extends MuiTableContainerProps {
  /**
   * Breakpoint up from which to allow line wrapping in table cells.
   *
   * @default 'lg'
   */
  allowWrappingFrom?: Breakpoint;

  children?: ReactNode;

  /**
   * Disable the padding negation feature, as described by the `paddingToNegate` prop. Disabling the negation results in legacy
   * behavior (up to UI 0.12 inclusive), which usually requires `minWidth` to be set on the TableContainer for proper
   * responsiveness.
   *
   * @default false
   */
  disablePaddingNegation?: boolean;

  /**
   * Padding of the containing element to negate for improved mobile UX (row borders will reach the edge of the containing
   * element when there is more content to that edge (i.e. after opening the page on XS, if the table is scrollable and content
   * would overflow the viewport, the table rows will ignore the right padding and instruct the user to scroll horizontally;
   * the same applies to the left padding once the user scrolls to the very right, and any potential positions in between).
   *
   * Defaults to default Paper paddings, as table(-container)s should usually be placed inside papers.
   */
  paddingToNegate?: Exclude<BoxProps['px'], Array<unknown>>;
}

interface TableContainerOwnerState {
  allowWrappingFrom: NonNullable<TableContainerProps['allowWrappingFrom']>;
  disablePaddingNegation: NonNullable<TableContainerProps['disablePaddingNegation']>;
  paddingToNegate: NonNullable<TableContainerProps['paddingToNegate']>;
}

const StyledTableContainer = styled(MuiTableContainer)<{ ownerState: TableContainerOwnerState }>(
  ({ theme, ownerState: { allowWrappingFrom, disablePaddingNegation, paddingToNegate } }) => {
    const wrapRules = {
      [`& .${tableCellClasses.root}, & .${tableCellClasses.root} > .${typographyClasses.root}`]: {
        overflowWrap: 'normal',
      },
      [theme.breakpoints.down(allowWrappingFrom)]: {
        whiteSpace: 'nowrap',
      },
    };

    if (disablePaddingNegation) return wrapRules;

    if (typeof paddingToNegate === 'number') {
      const oneSidePadding = theme.spacing(paddingToNegate);

      return {
        ...wrapRules,
        marginLeft: `-${oneSidePadding} !important`,
        width: `calc(100% + 2 * ${oneSidePadding})`,
        paddingLeft: oneSidePadding,
        paddingRight: oneSidePadding,
      };
    }

    if (typeof paddingToNegate !== 'object') {
      // misused -> the only thing this component will do (unless used properly) is disable line wraps
      if (process.env.NODE_ENV !== 'production')
        console.error('paddingToNegate on StyledTableContainer does is not a ResponsiveStyleValue - ignoring');

      return wrapRules;
    }

    return {
      ...wrapRules,

      // disable vertical scrollbar (usually caused by a horizontal one) on Virtuoso div
      '& > div': {
        overflowY: 'hidden',
      },

      ...Object.entries(paddingToNegate).reduce(
        // convert paddingToNegate to an actual value (preferably using css, not js)
        (prev, [breakpoint, value]) => {
          // nonsense passed
          if (!theme.breakpoints.keys.includes(breakpoint as Breakpoint) || value === null) return prev;

          const oneSidePadding = typeof value === 'number' ? theme.spacing(value) : value;

          return {
            ...prev,
            [theme.breakpoints.only(breakpoint as Breakpoint)]: {
              marginLeft: `-${oneSidePadding} !important`,
              width: `calc(100% + 2 * ${oneSidePadding})`,
              paddingLeft: oneSidePadding,
              paddingRight: oneSidePadding,
              /*
               * The original Figma prototype does not feature any spacing between the table and the containing element once the
               * user scrolls to the very right. To @miroslav.holecek, this isn't very intuitive, as the user might perceive that
               * there is still more content hidden to the right, and he thinks that a space should be there (same size as left).
               * Chrome puts that space there either way. To enable it in Firefox and Safari (and possibly other browsers), an
               * ::after has to be added to the <tr /> (one of them is enough, here we're targeting the one in <thead />) with a
               * width of 16px. If we do that, though, Chrome renders the space twice (both the implicit and the explicit one).
               * It is impossible to target only other browsers, as the '-webkit' vendor prefix targets Chrome, and the '-moz'
               * doesn't target Safari, which also has the 'issue' of not including the space by default. Leaving this here for
               * potential future reference.
               * [`& .${tableHeadClasses.root} .${tableRowClasses.root}::after`]: {
               *   '-moz-padding-end': oneSidePadding,
               *   '-webkit-padding-end': oneSidePadding,
               *   content: '""',
               * },
               */
            },
          };
        },
        {}
      ),
    };
  }
);

/**
 * TableContainer controls content breaking and padding negation. It also includes some rules for compatibility with
 * react-virtuoso.
 */
const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  (
    {
      allowWrappingFrom = 'lg',
      children,
      disablePaddingNegation = false,
      paddingToNegate = defaultPaperPaddings,
      ...props
    },
    ref
  ) => {
    return (
      <StyledTableContainer
        ownerState={{ allowWrappingFrom, disablePaddingNegation, paddingToNegate }}
        ref={ref}
        {...props}
      >
        {children}
      </StyledTableContainer>
    );
  }
);

TableContainer.displayName = 'TableContainer';

export default TableContainer;
